import { PrismButton, PrismCheckbox, PrismDatepicker, PrismDialog, PrismInputText, PrismInputTextarea, PrismSelect } from "@prism-ui/react";
import { TIMEZONE_OPTIONS } from "./ProjectFairs";
import type { EventDraft } from "./ProjectFairs";
import { usePreservingToggle } from "./usePreservingToggle";
import TimeOfDayPicker from "./TimeOfDayPicker";
import { utcIsoToZonedDate, utcIsoToZonedTime, zonedDateAndTimeToUtcIso } from "../../../../util/fairVisibility";
import { dayFromDatepicker, type DatepickerDetail } from "../../../DatePicker/utils";

interface SchedulePublishingModalProps {
  show: boolean;
  draft: EventDraft;
  saving: boolean;
  onChange: (updater: (prev: EventDraft) => EventDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}

// Formats a stored instant as MM/dd/yyyy for the picker's display value. The picker emits
// ISO strings back through prismChange, so this is display-only.
const toMonthDayYear = (isoValue: string | null | undefined, timezone?: string | null) => {
  const isoDate = utcIsoToZonedDate(isoValue, timezone);
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${month}/${day}/${year}`;
};

// Schedule & Publishing: the human-readable date/time blurb shown on the public page, plus
// the Go-Live date that automatically determines whether the fair page is visible.
//
// Go-Live stands alone because that is the ordinary case — a chapter turns its page on and
// leaves it on. A closing date is available but off by default, so nobody has to think about
// an ending they don't want. Both are interpreted in the fair's own timezone: Go-Live opens
// at the start of that day and the closing date runs through the end of its day.
const SchedulePublishingModal = ({ show, draft, saving, onChange, onSave, onCancel }: SchedulePublishingModalProps) => {
  const pageActive = draft.link_visibility?.pageActive;
  const timezone = pageActive?.timezone || "UTC";

  const patchPageActive = (patch: { fromUtc?: string; toUtc?: string; timezone?: string }) =>
    onChange((prev) => ({
      ...prev,
      link_visibility: {
        ...prev.link_visibility,
        pageActive: { ...prev.link_visibility?.pageActive, ...patch },
      },
    }));

  const [hasSecondTime, toggleSecondTime] = usePreservingToggle({
    open: show,
    initiallyOn: !!draft.friendly_time_2,
    snapshot: () => draft.friendly_time_2,
    clear: () => onChange((prev) => ({ ...prev, friendly_time_2: "" })),
    restore: (stashed: string) => onChange((prev) => ({ ...prev, friendly_time_2: stashed })),
  });

  const [hasGoLiveWindow, toggleGoLiveWindow] = usePreservingToggle({
    open: show,
    initiallyOn: !!pageActive?.fromUtc,
    snapshot: () => pageActive?.fromUtc || "",
    clear: () => patchPageActive({ fromUtc: "" }),
    restore: (stashed: string) => patchPageActive({ fromUtc: stashed }),
  });

  const [hasClosingDate, toggleClosingDate] = usePreservingToggle({
    open: show,
    initiallyOn: !!pageActive?.toUtc,
    snapshot: () => pageActive?.toUtc || "",
    clear: () => patchPageActive({ toUtc: "" }),
    restore: (stashed: string) => patchPageActive({ toUtc: stashed }),
  });

  // Changing the timezone must not move the dates the officer picked — re-anchor the same
  // calendar days in the new zone instead of letting the stored instants drift.
  const changeTimezone = (nextZone: string) => {
    const fromDate = utcIsoToZonedDate(pageActive?.fromUtc, timezone);
    const toDate = utcIsoToZonedDate(pageActive?.toUtc, timezone);
    const fromTime = utcIsoToZonedTime(pageActive?.fromUtc, timezone) || "00:00";
    const toTime = utcIsoToZonedTime(pageActive?.toUtc, timezone) || "23:59";
    patchPageActive({
      timezone: nextZone,
      fromUtc: fromDate ? zonedDateAndTimeToUtcIso(fromDate, fromTime, nextZone) : "",
      toUtc: toDate ? zonedDateAndTimeToUtcIso(toDate, toTime, nextZone) : "",
    });
  };

  return (
    <PrismDialog open={show} close-method="user" onPrismClose={onCancel} setAriaLabel="Schedule & Publishing">
      <span slot="header">Schedule &amp; Publishing</span>
      <div className="project-fairs-modal-body">
        <section className="project-fairs-tile">
          <h4>What attendees see</h4>
          <div className="project-fairs-grid">
            <PrismInputText
              label="Friendly Date"
              value={draft.friendly_date}
              onPrismInput={(e: CustomEvent<{ value: string }>) =>
                onChange((prev) => ({ ...prev, friendly_date: e.detail?.value || "" }))
              }
            />
            <PrismInputText
              label="Friendly Time"
              value={draft.friendly_time}
              onPrismInput={(e: CustomEvent<{ value: string }>) =>
                onChange((prev) => ({ ...prev, friendly_time: e.detail?.value || "" }))
              }
            />
          </div>
          <PrismCheckbox
            label="This fair runs in two sessions (for example, a morning block and an afternoon block)"
            checked={hasSecondTime}
            onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => toggleSecondTime(!!e.detail?.checked)}
          />
          {hasSecondTime ? (
            <div className="project-fairs-tile-fields">
              <PrismInputText
                label="Second Friendly Time"
                value={draft.friendly_time_2}
                onPrismInput={(e: CustomEvent<{ value: string }>) =>
                  onChange((prev) => ({ ...prev, friendly_time_2: e.detail?.value || "" }))
                }
              />
            </div>
          ) : null}
          <PrismInputTextarea
            label="Directions"
            rows={3}
            value={draft.directions}
            hint="Add practical arrival details, such as the room, floor, or building entrance."
            onPrismInput={(e: CustomEvent<{ value: string }>) =>
              onChange((prev) => ({ ...prev, directions: e.detail?.value || "" }))
            }
          />
        </section>

        <section className="project-fairs-tile">
          <h4>Go Live</h4>
          <p>
            The public fair page is published immediately and stays visible at all times. Turn this on to
            bound its visibility to a specific window instead.
          </p>
          <PrismCheckbox
            label="Limit this page to a specific date window"
            checked={hasGoLiveWindow}
            onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => toggleGoLiveWindow(!!e.detail?.checked)}
          />
          {hasGoLiveWindow ? (
            <div className="project-fairs-grid project-fairs-grid-aligned">
              <PrismDatepicker
                label="Go-Live date"
                value={toMonthDayYear(pageActive?.fromUtc, timezone)}
                onPrismChange={(e: CustomEvent<DatepickerDetail>) => {
                  const day = dayFromDatepicker(e.detail);
                  const time = utcIsoToZonedTime(pageActive?.fromUtc, timezone) || "00:00";
                  patchPageActive({ fromUtc: day ? zonedDateAndTimeToUtcIso(day, time, timezone) : "" });
                }}
              />
              <PrismSelect
                label="Timezone"
                value={timezone}
                options={TIMEZONE_OPTIONS}
                onPrismChange={(e: CustomEvent<{ value?: string }>) => changeTimezone(e.detail?.value || "UTC")}
              />
              <div className="project-fairs-time-row">
                <TimeOfDayPicker
                  legend="Go-Live time"
                  remountKey={`go-live-${show}-${pageActive?.fromUtc || ""}-${timezone}`}
                  value={utcIsoToZonedTime(pageActive?.fromUtc, timezone)}
                  onChange={(time) => {
                    const day = utcIsoToZonedDate(pageActive?.fromUtc, timezone);
                    if (day) patchPageActive({ fromUtc: zonedDateAndTimeToUtcIso(day, time, timezone) });
                  }}
                />
              </div>
            </div>
          ) : null}

          <PrismCheckbox
            label="Take the page back down on a specific date"
            checked={hasClosingDate}
            onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => toggleClosingDate(!!e.detail?.checked)}
          />
          {hasClosingDate ? (
            <div className="project-fairs-tile-fields">
              {!hasGoLiveWindow ? (
                <PrismSelect
                  label="Timezone"
                  value={timezone}
                  options={TIMEZONE_OPTIONS}
                  onPrismChange={(e: CustomEvent<{ value?: string }>) => changeTimezone(e.detail?.value || "UTC")}
                />
              ) : null}
              <PrismDatepicker
                label="Last day visible"
                value={toMonthDayYear(pageActive?.toUtc, timezone)}
                onPrismChange={(e: CustomEvent<DatepickerDetail>) => {
                  const day = dayFromDatepicker(e.detail);
                  const time = utcIsoToZonedTime(pageActive?.toUtc, timezone) || "23:59";
                  patchPageActive({ toUtc: day ? zonedDateAndTimeToUtcIso(day, time, timezone) : "" });
                }}
              />
            </div>
          ) : null}
        </section>
      </div>
      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onCancel}>
        Cancel
      </PrismButton>
      <PrismButton slot="footer" tone="primary-brand" display="fill" disabled={saving} loading={saving} onPrismClick={onSave}>
        Save
      </PrismButton>
    </PrismDialog>
  );
};

export default SchedulePublishingModal;
